const Math2D = require("../../core/util/Math2D");

// You should not directly use this file. It's the minimal representation of
// a geometry that GridGeometry turns into a full interface. Use GridGeometry.
// See SquareGridGeometryCore for any special notes about the interfaces for
// these functions.

// Hexagonal grid sizes are specified in rows and columns. There are
// cols*rows hexagonal cells, with the columns alternating between slightly to
// up and down (first column is up). In particular the cells are
// oriented so two sides are perfectly horizontal and no sides are vertical.
// - Cells are identified by [i, j] with 0 <= i < cols and 0 <= j < rows.
// - Vertices are identified by [i, j] with 0 <= i <= cols and 0 <= j <=
//   2*cols+1. Note that two of these vertices don't exist: [0, 2*cols+1], and either
//   [columns, 0] (if rows is even) or [columns, rows*2+1] (if rows is even).
// - Edges are identified by [i, j, ydir] with [i, j] a cell (possibly outside
//   the grid) and ydir one of -1, 0, 1. The edge emanates from the upper-left
//   vertex of the cell. Three edges come out of that vertex: ydir -1 means
//   up and left, ydir 1 means down and left, and ydir 0 means straight right.

// Cell bounding boxes have total width 1 and total height rt(3)/2 in the non-
// dim coordinate system. This means hexagons have side length 1/2.

// Rough ASCII diagram of cell/vertex coordinates in a 2 by 2 grid.
//
//    Cells & Vertices
//
//   0,0---1,0
//   /       \
// 0,1  0,0  1,1---2,1
//   \       /       \
//   0,2---1,2  1,0  2,2
//   /       \       /
// 0,3  0,1  1,3---2,3
//   \       /       \
//   0,4---1,4  1,1  2,4
//           \       /
//           1,5---2,5
//
//             Cells & Edges
//
//      /- 0,0,0 -\
//     /           \
//  0,0,+1       1,0,-1
//   /               \
//  <       0,0       >- 1,0,0 -\
//   \               /           \
//  0,1,-1       1,0,+1         2,1,-1
//     \           /               \
//      >- 0,1,0 -<       1,0       >
//     /           \               /
//  0,1,+1       1,1,-1         2,1,+1
//   /               \           /
//  <       0,1       >- 1,1,0 -<
//   \               /           \
//  0,2,-1       1,1,+1         2,2,-1
//     \           /               \
//      \- 0,2,0 -<       1,1       >
//                 \               /
//               1,2,-1         2,2,+1
//                   \           /
//                    \- 1,2,0 -/

// Constant used in a lot of computations.
const HRT3 = Math.sqrt(3) / 2;

/**
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const HexagonGridColumnsGeometryCore = function(cols, rows, options) {
	this.style = "hexColumns";
	this.cols = cols;
	this.rows = rows;
	this.options = options || {};
	this.noTop = !!this.options.noTop;
	this.noBottom = !!this.options.noBottom;
};

// Internal helper. Get range of cell y coordinates in [min, max + 1] form.
const getColumnCellRange = function(core, x) {
	return [
		core.noTop && x % 2 === 0 ? 1 : 0,
		core.rows - (core.noBottom && x % 2 !== 0 ? 1 : 0),
	];
};

HexagonGridColumnsGeometryCore.prototype.getBaseWidth = function() {
	return 0.75 * this.cols + 0.25;
};

HexagonGridColumnsGeometryCore.prototype.getBaseHeight = function() {
	const evenRange = getColumnCellRange(this, 0);
	const oddRange = getColumnCellRange(this, 1);
	const minY = Math.min(evenRange[0], oddRange[0] + 0.5);
	const maxY = Math.max(evenRange[1], oddRange[1] + 0.5);
	return (maxY - minY) * HRT3;
};

// This should return the cells in left-to-right then top-to-bottom order.
HexagonGridColumnsGeometryCore.prototype.getAllCells = function() {
	const result = [];
	for (let rowIndex = 0; rowIndex < this.rows; rowIndex++) {
		for (let colIndex = 0; colIndex < this.cols; colIndex++) {
			const colRange = getColumnCellRange(this, colIndex);
			if (rowIndex >= colRange[0] && rowIndex < colRange[1]) {
				result.push([colIndex, rowIndex]);
			}
		}
	}
	return result;
};

HexagonGridColumnsGeometryCore.prototype.convertEdgeZ = function(yDir) {
	return parseInt(yDir);
};

// Order matters: the edges must go clockwise around the cell
HexagonGridColumnsGeometryCore.prototype.getCellEdges = function(x, y) {
	const xParity = Math.abs(x % 2);
	return [
		[x, y, 0], // top left
		[x + 1, y + xParity, -1], // top right
		[x + 1, y + xParity, 1],
		[x, y + 1, 0],
		[x, y + 1, -1],
		[x, y, 1],
	];
};

// Note, coordinates are returned in ascending order with x taking priority over y
HexagonGridColumnsGeometryCore.prototype.getEdgeCells = function(x, y, yDir) {
	const xParity = Math.abs(x % 2);

	if (yDir === -1) {
		return [
			[x - 1, y - 1 + xParity],
			[x, y - 1],
		];
	} else if (yDir === 0) {
		return [
			[x, y - 1],
			[x, y],
		];
	} else if (yDir === 1) {
		return [
			[x - 1, y - 1 + xParity],
			[x, y],
		];
	} else {
		throw new Error("Invalid edge yDir " + yDir);
	}
};

HexagonGridColumnsGeometryCore.prototype.getEdgeVertices = function(
	x,
	y,
	yDir
) {
	const vertexY = 2 * y + Math.abs(x % 2);
	// [x, vertexY] is upper-left vertex (in vertex coordinates) of the cell [x, y].

	if (yDir === -1) {
		return [
			[x, vertexY - 1],
			[x, vertexY],
		];
	} else if (yDir === 0) {
		return [
			[x, vertexY],
			[x + 1, vertexY],
		];
	} else if (yDir === 1) {
		return [
			[x, vertexY],
			[x, vertexY + 1],
		];
	} else {
		throw new Error("Invalid edge yDir " + yDir);
	}
};

// Order matters: the edges must go clockwise around the vertex.
HexagonGridColumnsGeometryCore.prototype.getVertexEdges = function(vx, vy) {
	const xParity = Math.abs(vx % 2);
	// cell [x, cellY] is either the cell below (if on left side of horizontal segment)
	// or cell to the right (if on the right side)
	const cellY = Math.floor((vy - xParity) / 2);
	if ((vx + vy) % 2) {
		// Cell is to the right.
		return [
			[vx, cellY, 1],
			[vx, cellY + 1, -1],
			[vx - 1, cellY + xParity, 0],
		];
	} else {
		// Cell is below
		return [
			[vx, cellY, -1],
			[vx, cellY, 0],
			[vx, cellY, 1],
		];
	}
};

HexagonGridColumnsGeometryCore.prototype.getCollinearEdge = function(
	edge,
	vertex
) {
	return null;
};

HexagonGridColumnsGeometryCore.prototype.getDrawCellRect = function(dim, x, y) {
	const center = this.getDrawCellCenter(dim, x, y);
	return {
		x: center[0] - 0.375 * dim.size,
		y: center[1] - 0.375 * dim.size,
		w: 0.75 * dim.size,
		h: 0.75 * dim.size,
	};
};

HexagonGridColumnsGeometryCore.prototype.getDrawCellCenter = function(
	dim,
	x,
	y
) {
	const yNudge =
		(HRT3 / 2) * (1 + (this.noTop ? -1 : 0) + (x % 2 === 0 ? 0 : 1));
	return [
		dim.x + (0.5 + x * 0.75) * dim.size,
		dim.y + (yNudge + y * HRT3) * dim.size,
	];
};

HexagonGridColumnsGeometryCore.prototype.getDrawVertex = function(dim, x, y) {
	const xNudge = (x + y) % 2 === 0 ? 0.25 : 0;
	const yNudge = (HRT3 / 2) * (this.noTop ? -1 : 0);
	return [
		dim.x + (xNudge + x * 0.75) * dim.size,
		dim.y + (yNudge + (y * HRT3) / 2) * dim.size,
	];
};

HexagonGridColumnsGeometryCore.prototype.mouseToGrid = function(
	dim,
	mouseX,
	mouseY
) {
	return [
		(mouseX - dim.x) / (0.75 * dim.size),
		(mouseY - dim.y) / (HRT3 * dim.size) + (this.noTop ? 0.5 : 0),
	];
};

HexagonGridColumnsGeometryCore.prototype.gridToMouse = function(
	dim,
	coordX,
	coordY
) {
	coordY += this.noTop ? -0.5 : 0;
	return [coordX * dim.size * 0.75 + dim.x, coordY * dim.size * HRT3 + dim.y];
};

HexagonGridColumnsGeometryCore.prototype.getMousedCell = function(
	coordX,
	coordY,
	clamp
) {
	const shiftedDown = Math.floor(coordX) % 2;
	const realCoordY = coordY + (shiftedDown ? -0.5 : 0);
	const fx = coordX - Math.floor(coordX);
	const fy = realCoordY - Math.floor(realCoordY);

	// Preliminary cell coordinates.
	const cell = [Math.floor(coordX), Math.floor(realCoordY)];
	// Check if we're over slanted cell boundaries and make corrections.
	if (2 * fy + 3 * fx < 1) {
		if (!shiftedDown) {
			cell[1] -= 1;
		}
		cell[0] -= 1;
	} else if (2 * (1 - fy) + 3 * fx < 1) {
		if (shiftedDown) {
			cell[1] += 1;
		}
		cell[0] -= 1;
	}

	// If clamping, make sure we get valid coordinates.
	if (clamp) {
		cell[0] = Math.max(0, Math.min(this.cols - 1, cell[0]));
		const range = getColumnCellRange(this, cell[0]);
		cell[1] = Math.max(range[0], Math.min(range[1], cell[1]));
	}

	return cell;
};

HexagonGridColumnsGeometryCore.prototype.isDeadCenter = function(
	deadDistance,
	coordX,
	coordY,
	centerX,
	centerY
) {
	const dist = Math2D.pointPointDistance(coordX, coordY, centerX, centerY);
	return dist < deadDistance / 2;
};

HexagonGridColumnsGeometryCore.prototype.isDeadVertex = function(
	deadDistance,
	coordX,
	coordY,
	vertexX,
	vertexY
) {
	const dist = Math2D.pointPointDistance(coordX, coordY, vertexX, vertexY);
	return dist < deadDistance / 2;
};

HexagonGridColumnsGeometryCore.prototype.isLooseVertexFit = function(
	coordX,
	coordY,
	vertexX,
	vertexY
) {
	const dx = vertexX - coordX;
	const dy = vertexY - coordY;
	const squareDist = 0.5625 * dx * dx + 0.75 * dy * dy;
	return squareDist > 0.05;
};

HexagonGridColumnsGeometryCore.prototype.isNewEdge = function(
	coordX,
	coordY,
	newEdge,
	lastEdge
) {
	// Later if we want to do some sensitive checking of new edges, we can add this.
	return true;
};

module.exports = HexagonGridColumnsGeometryCore;
